import React, { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isFuture, startOfDay, format } from "date-fns";
import { useFormik } from "formik";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  MenuItem,
  IconButton,
} from "@mui/material";
import { X } from "@phosphor-icons/react";
import { ANDatePicker } from "@answersnow/pkg-theme";
import { useGlobalToast } from "components/GlobalToastProvider";
import useToggleClientActivation from "hooks/api/client/useToggleClientActivation";
import useCancelFutureDeactivation from "hooks/api/client/useCancelFutureDeactivation";
import { useGetScheduledDeactivation } from "hooks/api/client/useScheduledDeactivation";
import { useClientBillableUnitsStatus } from "hooks/api/client/useClientBillableUnitsStatus";
import actions from "actions";
import {
  getCustomerDeactivationReasons,
  getCustomerDepartureOptions,
  getCustomerDetails,
} from "selectors";

export type DialogTriggerMode = "edit" | "confirm_from_alert" | "confirm_from_notification" | "new_deactivation";

export interface ClientDeactivationDialogProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  clientName?: string;
  clientLastName?: string;
  triggerMode: DialogTriggerMode;
  onSuccess?: () => void;
  customerDetails?: any;
  scheduledDeactivationData?: any;
  hasScheduledCall?: boolean;
}

/**
 * Unified dialog component for client deactivation management.
 * This component handles multiple scenarios:
 * 1. Edit scheduled deactivation (from alert edit button)
 * 2. Confirm deactivation on scheduled date (from alert)
 * 3. Confirm deactivation from notification
 *
 * The dialog adapts its UI and behavior based on the trigger mode and selected date.
 */
const ClientDeactivationDialog: React.FC<ClientDeactivationDialogProps> = ({
  open,
  onClose,
  clientId,
  clientName,
  clientLastName,
  triggerMode,
  onSuccess,
  customerDetails: propCustomerDetails,
  scheduledDeactivationData: propScheduledDeactivationData,
  hasScheduledCall = false,
}) => {
  const dispatch = useDispatch();
  const { showToast } = useGlobalToast();

  // Get reason lists from Redux
  const customerDropReasons = useSelector((state: any) =>
    getCustomerDeactivationReasons(state, "drop")
  );
  const customerChurnReasons = useSelector((state: any) =>
    getCustomerDeactivationReasons(state, "churn")
  );
  const customerDepartureOptions = useSelector((state: any) =>
    getCustomerDepartureOptions(state, "main")
  );

  // Get customer details from Redux if not provided as prop
  const reduxCustomerDetails = useSelector(getCustomerDetails);
  const customerDetails = propCustomerDetails || reduxCustomerDetails;

  // Fetch scheduled deactivation data
  // Only fetch if not already provided as prop
  const { data: fetchedScheduledDeactivationData, refetch: refetchScheduledDeactivation } =
    useGetScheduledDeactivation({
      clientId,
      enabled: open && !!clientId && !propScheduledDeactivationData,
    });

  // Fetch billable units status
  const { data: billableUnitsStatus } = useClientBillableUnitsStatus({
    clientId,
    enabled: open && !!clientId,
  });

  // Extract user ID from customer details
  const userId = customerDetails?.details?.userid;

  // Track if we've loaded data for this dialog session
  const hasLoadedData = React.useRef(false);

  // Load reason lists and client data when dialog opens (only once per session)
  useEffect(() => {
    if (open && !hasLoadedData.current) {
      // Always load dropdown options (reasons and departure options)
      dispatch(actions.getCustomerDeactivationReasons());
      dispatch(actions.getCustomerDepartureOptions());

      // Only load customer details if not already provided
      if (clientId && !propCustomerDetails) {
        dispatch(actions.loadCustomerDetails(clientId));
      }

      hasLoadedData.current = true;
    }

    // Reset when dialog closes
    if (!open) {
      hasLoadedData.current = false;
    }
  }, [open, clientId, dispatch, propCustomerDetails]);

  // Use prop data if provided, otherwise use fetched data
  const scheduledDeactivationData = propScheduledDeactivationData || fetchedScheduledDeactivationData;

  // Helper function to parse scheduled deactivation data into initial values
  const parseScheduledDeactivationData = useCallback(() => {
    if (!scheduledDeactivationData) {
      return {
        dropReason: "",
        dischargeReason: "",
        departedTo: "",
        disabledDate: null as Date | null,
      };
    }

    // Handle both prop data and fetched data structures
    const scheduledData = scheduledDeactivationData.data || scheduledDeactivationData;

    const loadedDropReason = scheduledData.drop_reason || "";
    const loadedDischargeReason = scheduledData.churn_reason || "";
    const loadedDepartedTo = scheduledData.departed_to || "";
    let loadedDate: Date | null = null;

    if (scheduledData.scheduled_deactivation_date) {
      if (scheduledData.scheduled_deactivation_date instanceof Date) {
        loadedDate = scheduledData.scheduled_deactivation_date;
      } else {
        const dateStr = scheduledData.scheduled_deactivation_date.split('T')[0];
        const [year, month, day] = dateStr.split('-').map(Number);
        loadedDate = new Date(year, month - 1, day);
      }
    }

    return {
      dropReason: loadedDropReason,
      dischargeReason: loadedDischargeReason,
      departedTo: loadedDepartedTo,
      disabledDate: loadedDate,
    };
  }, [scheduledDeactivationData]);

  // Check if client has had billable units
  const hasBillableUnits = useMemo(() => {
    // Use the billable units status from the dedicated endpoint
    return billableUnitsStatus?.has_billable_units ?? false;
  }, [billableUnitsStatus]);

  // For new_deactivation mode, always show departed_to field
  const isNewDeactivation = triggerMode === "new_deactivation";

  // Get initial values from scheduled deactivation data
  const formInitialValues = useMemo(() => {
    return parseScheduledDeactivationData();
  }, [parseScheduledDeactivationData]);

  // Determine which reason field to show based on form values
  const determineReasonFields = (dropReason: string, dischargeReason: string) => {
    const hasDropReasonValue = dropReason && typeof dropReason === 'string' && dropReason.trim() !== '';
    const hasDischargeReasonValue = dischargeReason && typeof dischargeReason === 'string' && dischargeReason.trim() !== '';

    let showDropReason: boolean;
    let showDischargeReason: boolean;

    if (hasDropReasonValue && !hasDischargeReasonValue) {
      showDropReason = true;
      showDischargeReason = false;
    } else if (hasDischargeReasonValue && !hasDropReasonValue) {
      showDropReason = false;
      showDischargeReason = true;
    } else if (hasDropReasonValue && hasDischargeReasonValue) {
      showDropReason = true;
      showDischargeReason = false;
    } else {
      showDropReason = !hasBillableUnits;
      showDischargeReason = hasBillableUnits;
    }

    return { showDropReason, showDischargeReason };
  };

  // Structure discharge reasons by category
  const dischargeReasonsByCategory = useMemo(() => {
    if (!customerChurnReasons) return { answersNowInitiated: [], clientInitiated: [] };

    const answersNowReasons = [
      "Graduation",
      "Client not a fit for online treatment",
      "Family commitment/attendance concerns"
    ];

    const clientReasons = [
      "High cost/no insurance coverage",
      "No longer wanting ABA services",
      "Transitioning to an in-person provider",
      "Family is no longer responsive"
    ];

    return {
      answersNowInitiated: customerChurnReasons.filter(
        (reason: any) => answersNowReasons.includes(reason.reason)
      ),
      clientInitiated: customerChurnReasons.filter(
        (reason: any) => clientReasons.includes(reason.reason)
      ),
    };
  }, [customerChurnReasons]);

  // Check if departure field is required
  const requiresDepartureField = (reason: string) => {
    return reason === "Transitioning to an in-person provider";
  };

  // Formik configuration
  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
    touched,
    values,
    setFieldValue,
    dirty,
    isSubmitting,
  } = useFormik({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validate: (values) => {
      const errors: any = {};
      // Re-validate with current values for conditional validation
      const { showDropReason, showDischargeReason } = determineReasonFields(
        values.dropReason,
        values.dischargeReason
      );
      const isDepartureRequired = requiresDepartureField(values.dischargeReason);

      // Check for date first - this is the primary requirement
      if (!values.disabledDate) {
        errors.disabledDate = "Deactivation date is required";
      } else if (isNewDeactivation && hasScheduledCall) {
        // Only check for scheduled calls if a date has been selected
        errors.disabledDate = "The client has scheduled calls. Please cancel all calls before deactivation.";
      }

      if (showDropReason && !values.dropReason) {
        errors.dropReason = "Drop reason is required";
      }

      if (showDischargeReason && !values.dischargeReason) {
        errors.dischargeReason = "Discharge reason is required";
      }

      if (isDepartureRequired && !values.departedTo) {
        errors.departedTo = "Where did they go? is required";
      }

      return errors;
    },
    onSubmit: (formValues) => {
      // Determine which action to take based on trigger mode and date
      const isFutureDate = formValues.disabledDate ? isFuture(startOfDay(formValues.disabledDate)) : false;

      let shouldConfirm = false;

      if (triggerMode === "new_deactivation") {
        shouldConfirm = !isFutureDate && formValues.disabledDate !== null;
      } else if (triggerMode === "edit") {
        shouldConfirm = !isFutureDate && formValues.disabledDate !== null;
      } else {
        // In confirm modes, show confirm by default unless fields changed and date is future
        // Use Formik's dirty flag instead of manually calculating changes
        shouldConfirm = !dirty || (!isFutureDate && formValues.disabledDate !== null);
      }

      if (shouldConfirm) {
        handleDeactivation(formValues, true);
      } else {
        handleDeactivation(formValues, false);
      }
    },
  });

  // Determine which reason fields to show based on current form values
  const { showDropReason, showDischargeReason } = determineReasonFields(
    values.dropReason,
    values.dischargeReason
  );
  const isDepartureRequired = requiresDepartureField(values.dischargeReason);

  // Check if the selected deactivation date is in the future
  const isFutureDeactivation = useMemo(() => {
    if (!values.disabledDate) return false;
    return isFuture(startOfDay(values.disabledDate));
  }, [values.disabledDate]);

  // Format scheduled deactivation date for display
  const formattedDeactivationDate = useMemo(() => {
    if (!values.disabledDate) return null;

    try {
      return values.disabledDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return values.disabledDate.toString();
    }
  }, [values.disabledDate]);

  // Initialize mutation hooks
  const { toggleActivation, isLoadingToggleActivation } = useToggleClientActivation({
    onSuccess: () => {
      const wasScheduledDeactivation = isFutureDeactivation;
      const toastMessage = wasScheduledDeactivation
        ? `Success! Client deactivation scheduled for ${formattedDeactivationDate}`
        : "Success! Client account deactivated";

      showToast({
        message: toastMessage,
      });

      onClose();
      dispatch(actions.loadCustomerDetails(clientId));
      onSuccess?.();
      refetchScheduledDeactivation();
    },
  });

  const { cancelDeactivation, isLoadingCancelDeactivation } = useCancelFutureDeactivation({
    onSuccess: () => {
      showToast({
        message: "Future deactivation cancelled successfully",
      });
      onClose();
      dispatch(actions.loadCustomerDetails(clientId));
      onSuccess?.();
      refetchScheduledDeactivation();
    },
  });

  // Handle deactivation (for both saving changes and confirming deactivation)
  const handleDeactivation = useCallback((formValues: typeof values, useTodayDate: boolean = false) => {
    if (!userId || !clientId) {
      showToast({
        message: "Error: Unable to process request. Missing client information.",
        errorState: true,
      });
      return;
    }

    if (!useTodayDate && !formValues.disabledDate) {
      showToast({
        message: "Error: Deactivation date is required.",
        errorState: true,
      });
      return;
    }

    // Use today's date for immediate deactivation, or form date for scheduled deactivation
    // (using date-fns to avoid timezone issues)
    const dateString = useTodayDate
      ? format(new Date(), 'yyyy-MM-dd')
      : format(formValues.disabledDate!, 'yyyy-MM-dd');

    const payload = {
      id: userId,
      clientId,
      dropReason: formValues.dropReason || undefined,
      churnReason: formValues.dischargeReason || undefined,
      note: "",
      departedTo: formValues.departedTo || undefined,
      disabledDate: dateString,
    };

    toggleActivation(payload);
  }, [userId, clientId, toggleActivation, showToast]);

  // Handle cancel deactivation
  const handleCancelDeactivation = useCallback(() => {
    if (!clientId) {
      showToast({
        message: "Error: Unable to process request. Missing client information.",
        errorState: true,
      });
      return;
    }
    cancelDeactivation(clientId);
  }, [clientId, cancelDeactivation, showToast]);

  // Get full client name
  const fullClientName = clientLastName
    ? `${clientName} ${clientLastName}`
    : clientName ||
    (customerDetails?.details?.child_last_name
      ? `${customerDetails?.details?.name} ${customerDetails?.details?.child_last_name}`
      : customerDetails?.details?.name) ||
    'Client';

  // Determine dialog title based on trigger mode
  const dialogTitle = useMemo(() => {
    if (triggerMode === "new_deactivation") {
      return "Deactivate Client";
    }
    if (triggerMode === "edit") {
      return "Edit Client Deactivation";
    }
    return "Confirm Client Deactivation";
  }, [triggerMode]);

  // Determine if we should show the confirm button or save changes button
  // Logic:
  // - In new_deactivation mode: Show "Deactivate" or "Set Future Deactivation Date" based on date
  // - In confirm modes: Show "Confirm And Deactivate" (red) by default, change to "Save Changes" only if any field was modified
  // - In edit mode: Show "Save Changes" if date is future, or "Confirm And Deactivate" if date is today/past
  const showConfirmButton = useMemo(() => {
    if (triggerMode === "new_deactivation") {
      // For new deactivations, always show deactivate/schedule button
      return !isFutureDeactivation && values.disabledDate !== null;
    }
    if (triggerMode === "edit") {
      // In edit mode, only show confirm if date is changed to today or past
      return !isFutureDeactivation && values.disabledDate !== null;
    }

    // In confirm modes (confirm_from_alert, confirm_from_notification)
    // Show "Confirm And Deactivate" by default (when no fields changed)
    // Change to "Save Changes" only if user has modified any field
    if (!dirty) {
      // Default: show confirm button (red)
      return values.disabledDate !== null;
    } else {
      // User made changes: show save button (unless date is today/past, then still confirm)
      return !isFutureDeactivation && values.disabledDate !== null;
    }
  }, [triggerMode, isFutureDeactivation, values.disabledDate, dirty]);

  // Determine the primary action handler and label
  const primaryActionLabel = useMemo(() => {
    if (triggerMode === "new_deactivation") {
      return isFutureDeactivation ? "Set Future Deactivation Date" : "Deactivate Client";
    }
    return showConfirmButton ? "Confirm And Deactivate" : "Save Changes";
  }, [triggerMode, isFutureDeactivation, showConfirmButton]);
  const primaryActionColor = (showConfirmButton || triggerMode === "new_deactivation") ? "error" : "primary";


  // Handle dialog close with form reset
  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="client-deactivation-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="client-deactivation-dialog-title">
        {dialogTitle}
        <IconButton aria-label="close" onClick={handleClose} size="small" disabled={isSubmitting}>
          <X />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {/* Warning alert for confirming scheduled deactivation */}
          {(triggerMode === "confirm_from_alert" || triggerMode === "confirm_from_notification") && (
            <Alert severity="warning" style={{ marginBottom: "20px" }}>
              {fullClientName} is scheduled for deactivation. Please review and confirm the reason before we proceed.
            </Alert>
          )}

          {(triggerMode === "confirm_from_alert" || triggerMode === "confirm_from_notification") && (
            <Box>
              <Typography variant="body2" style={{ marginBottom: "4px", fontWeight: 500 }}>
                Client
              </Typography>
              <Typography variant="body2">
                {fullClientName}
              </Typography>
            </Box>
          )}

          {/* Drop reason field for clients without billable units */}
          {showDropReason && (
            <TextField
              select
              fullWidth
              label="Drop reason"
              name="dropReason"
              value={values.dropReason}
              onChange={handleChange}
              onBlur={handleBlur}
              margin="normal"
              required
              error={touched.dropReason && !!errors.dropReason}
              helperText={touched.dropReason && errors.dropReason}
              sx={{ marginTop: "0" }}
            >
              {customerDropReasons && customerDropReasons.map((option: any) => (
                <MenuItem key={option.id} value={option.reason}>
                  {option.reason}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Discharge reason field for clients with billable units */}
          {showDischargeReason && (
            <TextField
              select
              fullWidth
              label="Discharge reason"
              name="dischargeReason"
              value={values.dischargeReason}
              onChange={handleChange}
              onBlur={handleBlur}
              margin="normal"
              required
              error={touched.dischargeReason && !!errors.dischargeReason}
              helperText={touched.dischargeReason && errors.dischargeReason}
              sx={{ marginTop: "0" }}
            >
              {/* AnswersNow Initiated Reasons */}
              <MenuItem disabled>
                <Typography variant="body2">
                  AnswersNow Initiated Reasons
                </Typography>
              </MenuItem>
              {dischargeReasonsByCategory.answersNowInitiated.map((option: any) => (
                <MenuItem key={option.id} value={option.reason} style={{ paddingLeft: '44px' }}>
                  {option.reason}
                </MenuItem>
              ))}

              {/* Client Initiated Reasons */}
              <MenuItem disabled>
                <Typography variant="body2">
                  Client Initiated Reasons
                </Typography>
              </MenuItem>
              {dischargeReasonsByCategory.clientInitiated.map((option: any) => (
                <MenuItem key={option.id} value={option.reason} style={{ paddingLeft: '44px' }}>
                  {option.reason}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Departed to field - always show for new deactivations, or when required/has value */}
          {(isNewDeactivation || isDepartureRequired || values.departedTo) && (
            <TextField
              select
              fullWidth
              label="Where did they go?"
              name="departedTo"
              value={values.departedTo}
              onChange={handleChange}
              onBlur={handleBlur}
              margin="normal"
              required={isDepartureRequired}
              error={touched.departedTo && !!errors.departedTo}
              helperText={touched.departedTo && errors.departedTo}
            >
              {customerDepartureOptions && customerDepartureOptions.map((option: any) => (
                <MenuItem key={option.id} value={option.option}>
                  {option.option}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Deactivation date picker */}
          <ANDatePicker
            id="client-deactivation-date-picker"
            value={values.disabledDate}
            onChange={(date: Date | null) => setFieldValue("disabledDate", date, true)}
            onBlur={handleBlur}
            fullWidth={true}
            format="PP"
            placeholderText="Choose a date"
            label="Deactivation date"
            sx={{ marginTop: "16px" }}
            slotProps={{
              field: {
                fullWidth: true,
                error: touched.disabledDate && !!errors.disabledDate,
                helperText: touched.disabledDate && errors.disabledDate,
              },
              popper: {
                disablePortal: false,
                style: { zIndex: 1300 },
              },
            }}
          />

          {/* Alert for new_deactivation mode */}
          {isNewDeactivation && hasScheduledCall && (
            <Alert severity="error" style={{ marginTop: "10px" }}>
              The client has scheduled calls. Please cancel all calls before deactivation.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!isNewDeactivation && (
            <Button
              onClick={handleCancelDeactivation}
              disabled={isLoadingCancelDeactivation || isSubmitting}
              variant="text"
              color="error"
              size="medium"
              type="button"
            >
              {isLoadingCancelDeactivation ? "Cancelling..." : "Cancel Deactivation"}
            </Button>
          )}
          <Button
            onClick={handleClose}
            variant="contained"
            color="secondary"
            size="medium"
            type="button"
            disabled={isSubmitting}
          >
            {isNewDeactivation ? "Cancel" : "Close"}
          </Button>
          <Button
            disabled={!values.disabledDate || (isNewDeactivation && hasScheduledCall) || isLoadingToggleActivation || isSubmitting}
            color={primaryActionColor}
            variant="contained"
            size="medium"
            type="submit"
          >
            {isLoadingToggleActivation || isSubmitting ? "Processing..." : primaryActionLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ClientDeactivationDialog;
